import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  Store, 
  MessageCircle, 
  Award,
  Fish,
  Plus,
  Send,
  Trash2,
  Edit,
  Crown,
  LogOut,
  BarChart3,
  Gift,
  X,
  Bell,
  Megaphone,
  Tag,
  UserPlus,
  Shield,
  Image,
  Loader2,
  Upload
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [catches, setCatches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [retailerDialogOpen, setRetailerDialogOpen] = useState(false);
  const [retailerForm, setRetailerForm] = useState({
    name: "",
    description: "",
    discount_percent: 10,
    website: "",
    location: ""
  });
  const [retailerLogo, setRetailerLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editingRetailer, setEditingRetailer] = useState(null);
  
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({
    title: "",
    content: "",
    message_type: "announcement"
  });

  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    email: "",
    password: "",
    name: "",
    surname: ""
  });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [compressingImages, setCompressingImages] = useState(false);
  const [migratingPhotos, setMigratingPhotos] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, membersRes, retailersRes, questionsRes, catchesRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/members`, { headers }),
        axios.get(`${API}/retailers`),
        axios.get(`${API}/admin/qa`, { headers }),
        axios.get(`${API}/catches/feed?limit=50`),
        axios.get(`${API}/admin/messages`, { headers })
      ]);
      
      setStats(statsRes.data);
      setMembers(membersRes.data);
      setRetailers(retailersRes.data);
      setQuestions(questionsRes.data);
      setCatches(catchesRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleGrantFreeMembership = async (userId, planType = "annual") => {
    try {
      await axios.post(`${API}/admin/grant-free-membership/${userId}?plan_type=${planType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Free ${planType} membership granted!`);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to grant membership");
    }
  };

  const handleRevokeMembership = async (userId) => {
    if (!window.confirm("Revoke this member's subscription?")) return;
    try {
      await axios.post(`${API}/admin/revoke-membership/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Membership revoked");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to revoke membership");
    }
  };

  const handleRetailerSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("name", retailerForm.name);
      formData.append("description", retailerForm.description || "");
      formData.append("discount_percent", retailerForm.discount_percent);
      formData.append("website", retailerForm.website || "");
      formData.append("location", retailerForm.location || "");
      if (retailerLogo) {
        formData.append("logo", retailerLogo);
      }

      if (editingRetailer) {
        await axios.put(`${API}/retailers/${editingRetailer.id}`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
        toast.success("Retailer updated");
      } else {
        await axios.post(`${API}/retailers`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
        toast.success("Retailer added");
      }
      setRetailerDialogOpen(false);
      setRetailerForm({ name: "", description: "", discount_percent: 10, website: "", location: "" });
      setRetailerLogo(null);
      setLogoPreview(null);
      setEditingRetailer(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save retailer");
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo must be under 2MB");
        return;
      }
      setRetailerLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/messages`, messageForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message sent to all members!");
      setMessageDialogOpen(false);
      setMessageForm({ title: "", content: "", message_type: "announcement" });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!adminForm.email || !adminForm.password || !adminForm.name || !adminForm.surname) {
      toast.error("Please fill in all fields");
      return;
    }
    if (adminForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreatingAdmin(true);
    try {
      await axios.post(`${API}/admin/create-admin`, adminForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin account created successfully!");
      setAdminDialogOpen(false);
      setAdminForm({ email: "", password: "", name: "", surname: "" });
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create admin";
      toast.error(message);
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteRetailer = async (id) => {
    if (!window.confirm("Delete this retailer?")) return;
    try {
      await axios.delete(`${API}/retailers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Retailer deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete retailer");
    }
  };

  const handleToggleAdmin = async (userId, userName) => {
    const member = members.find(m => m.id === userId);
    const action = member?.is_admin ? "revoke admin rights from" : "give admin rights to";
    if (!window.confirm(`Are you sure you want to ${action} ${userName}?`)) return;
    
    try {
      const response = await axios.post(`${API}/admin/toggle-admin/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update admin status";
      toast.error(message);
    }
  };

  const handleDeleteCatch = async (catchId, catchName, userName) => {
    if (!window.confirm(`Delete "${catchName}" by ${userName}? This cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(`${API}/admin/catches/${catchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete catch");
    }
  };

  const handleRespondToQuestion = async (qaId, response) => {
    try {
      const formData = new FormData();
      formData.append("response", response);
      
      await axios.post(`${API}/admin/qa/${qaId}/respond`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Response sent");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to send response");
    }
  };

  const handleSetMemberOfMonth = async (userId, catchId = null) => {
    try {
      await axios.post(`${API}/admin/member-of-month/${userId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: catchId ? { catch_id: catchId } : {}
      });
      toast.success("Member of the Month set!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to set Member of the Month");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleCompressImages = async () => {
    if (!window.confirm("This will compress all existing photos to reduce file sizes and improve loading times. Continue?")) return;
    
    setCompressingImages(true);
    try {
      const response = await axios.post(`${API}/admin/compress-images`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to compress images");
    } finally {
      setCompressingImages(false);
    }
  };

  const handleMigratePhotos = async () => {
    if (!window.confirm("This will migrate all existing photos to the database for permanent storage. Photos will never disappear again. Continue?")) return;
    
    setMigratingPhotos(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-photos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to migrate photos");
    } finally {
      setMigratingPhotos(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-main)]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-main)]">
      {/* Header */}
      <header className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-gray-100 z-30 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.jpg" alt="FishingFanatix" className="h-10 w-auto" />
            <div>
              <h1 className="text-xl font-bold text-[var(--deep-ocean)]">Admin Dashboard</h1>
              <p className="text-sm text-[var(--river-stone)]">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="text-green-600 border-green-600 hover:bg-green-50"
              onClick={handleMigratePhotos}
              disabled={migratingPhotos}
              data-testid="migrate-photos-btn"
            >
              {migratingPhotos ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <Image className="w-4 h-4 mr-2" />
                  Fix Photos
                </>
              )}
            </Button>
            <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-[var(--deep-ocean)] border-[var(--deep-ocean)]"
                  data-testid="create-admin-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Admin
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-[var(--deep-ocean)]">Create New Admin</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateAdmin} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin-name">First Name</Label>
                      <Input
                        id="admin-name"
                        placeholder="John"
                        value={adminForm.name}
                        onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                        data-testid="admin-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-surname">Surname</Label>
                      <Input
                        id="admin-surname"
                        placeholder="Smith"
                        value={adminForm.surname}
                        onChange={(e) => setAdminForm({ ...adminForm, surname: e.target.value })}
                        data-testid="admin-surname-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      placeholder="admin@example.com"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                      data-testid="admin-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-password">Password</Label>
                    <Input
                      id="admin-password"
                      type="password"
                      placeholder="Min 6 characters"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                      data-testid="admin-password-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setAdminDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="btn-primary" disabled={creatingAdmin} data-testid="submit-admin-btn">
                      {creatingAdmin ? "Creating..." : "Create Admin"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              data-testid="view-member-dashboard-btn"
            >
              Member View
            </Button>
            <Button
              variant="ghost"
              className="text-red-500"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Total Members", value: stats?.total_members || 0, icon: Users, color: "bg-blue-500" },
            { label: "Active Members", value: stats?.active_members || 0, icon: Crown, color: "bg-green-500" },
            { label: "Total Catches", value: stats?.total_catches || 0, icon: Fish, color: "bg-purple-500" },
            { label: "Pending Q&A", value: stats?.pending_questions || 0, icon: MessageCircle, color: "bg-orange-500" },
            { label: "Retailers", value: stats?.total_retailers || 0, icon: Store, color: "bg-cyan-500" },
          ].map((stat) => (
            <Card key={stat.label} className="bg-white card-shadow border-0">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--deep-ocean)]">{stat.value}</p>
                    <p className="text-xs text-[var(--river-stone)]">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="members" className="space-y-6">
          <TabsList className="bg-white card-shadow">
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="retailers" data-testid="tab-retailers">Retailers</TabsTrigger>
            <TabsTrigger value="qa" data-testid="tab-qa">Q&A</TabsTrigger>
            <TabsTrigger value="catches" data-testid="tab-catches">Catches</TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card className="bg-white card-shadow border-0">
              <CardHeader>
                <CardTitle className="text-[var(--deep-ocean)]">All Members</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--deep-ocean)]">Member</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--deep-ocean)]">Member #</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--deep-ocean)]">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--deep-ocean)]">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--deep-ocean)]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {member.is_member_of_month && <Award className="w-4 h-4 text-yellow-500" />}
                              {member.is_admin && <Shield className="w-4 h-4 text-[var(--sunrise-orange)]" title="Admin" />}
                              <span className="font-medium text-[var(--deep-ocean)]">{member.name} {member.surname}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-mono text-sm text-[var(--river-stone)]">{member.member_number}</td>
                          <td className="py-3 px-4 text-sm text-[var(--river-stone)]">{member.email}</td>
                          <td className="py-3 px-4">
                            <Badge className={member.subscription_status === "active" ? "bg-[var(--success)]" : member.subscription_status === "trial" ? "bg-blue-500" : "bg-yellow-500"}>
                              {member.subscription_status}
                            </Badge>
                            {member.is_admin && (
                              <Badge className="ml-2 bg-[var(--sunrise-orange)]">admin</Badge>
                            )}
                            {member.subscription_type && !member.is_admin && (
                              <span className="ml-2 text-xs text-[var(--river-stone)]">
                                ({member.subscription_type})
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2 flex-wrap">
                              {/* Toggle Admin Button - show for all non-self users */}
                              {member.id !== user?.id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={member.is_admin 
                                    ? "text-red-600 border-red-600 hover:bg-red-50" 
                                    : "text-[var(--sunrise-orange)] border-[var(--sunrise-orange)] hover:bg-orange-50"}
                                  onClick={() => handleToggleAdmin(member.id, `${member.name} ${member.surname}`)}
                                  data-testid={`toggle-admin-${member.id}`}
                                >
                                  <Shield className="w-3 h-3 mr-1" />
                                  {member.is_admin ? "Remove Admin" : "Make Admin"}
                                </Button>
                              )}
                              {!member.is_admin && member.subscription_status !== "active" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 border-green-600 hover:bg-green-50"
                                    onClick={() => handleGrantFreeMembership(member.id, "annual")}
                                    data-testid={`grant-free-${member.id}`}
                                  >
                                    <Gift className="w-3 h-3 mr-1" /> Free Annual
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                    onClick={() => handleGrantFreeMembership(member.id, "monthly")}
                                    data-testid={`grant-free-monthly-${member.id}`}
                                  >
                                    <Gift className="w-3 h-3 mr-1" /> Free Monthly
                                  </Button>
                                </>
                              )}
                              {!member.is_admin && member.subscription_status === "active" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={() => handleRevokeMembership(member.id)}
                                  data-testid={`revoke-${member.id}`}
                                >
                                  <X className="w-3 h-3 mr-1" /> Revoke
                                </Button>
                              )}
                              {!member.is_admin && !member.is_member_of_month && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSetMemberOfMonth(member.id)}
                                  data-testid={`set-mom-${member.id}`}
                                >
                                  <Crown className="w-3 h-3 mr-1" /> MoM
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="bg-white card-shadow border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-[var(--deep-ocean)]">Broadcast Messages</CardTitle>
                <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="create-message-btn">
                      <Plus className="w-4 h-4 mr-2" /> New Message
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send Message to All Members</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleMessageSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Message Type</Label>
                        <Select 
                          value={messageForm.message_type} 
                          onValueChange={(value) => setMessageForm({ ...messageForm, message_type: value })}
                        >
                          <SelectTrigger className="input-field" data-testid="message-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="announcement">
                              <div className="flex items-center gap-2">
                                <Megaphone className="w-4 h-4" /> Announcement
                              </div>
                            </SelectItem>
                            <SelectItem value="promotion">
                              <div className="flex items-center gap-2">
                                <Tag className="w-4 h-4" /> Partner Promotion
                              </div>
                            </SelectItem>
                            <SelectItem value="alert">
                              <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4" /> Alert
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input
                          value={messageForm.title}
                          onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                          className="input-field"
                          placeholder="e.g., New Partner Discount!"
                          required
                          data-testid="message-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Message Content *</Label>
                        <Textarea
                          value={messageForm.content}
                          onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                          className="input-field min-h-[120px]"
                          placeholder="Write your message here..."
                          required
                          data-testid="message-content-input"
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary" data-testid="send-message-btn">
                        <Send className="w-4 h-4 mr-2" /> Send to All Members
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div key={msg.id} className="p-4 bg-[var(--bg-main)] rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {msg.message_type === "announcement" && <Megaphone className="w-4 h-4 text-blue-500" />}
                            {msg.message_type === "promotion" && <Tag className="w-4 h-4 text-green-500" />}
                            {msg.message_type === "alert" && <Bell className="w-4 h-4 text-red-500" />}
                            <h4 className="font-semibold text-[var(--deep-ocean)]">{msg.title}</h4>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500"
                            onClick={() => handleDeleteMessage(msg.id)}
                            data-testid={`delete-message-${msg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-[var(--river-stone)] mb-2">{msg.content}</p>
                        <div className="flex items-center gap-4 text-xs text-[var(--river-stone)]">
                          <span>By: {msg.created_by}</span>
                          <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                          <Badge className={
                            msg.message_type === "announcement" ? "bg-blue-100 text-blue-800" :
                            msg.message_type === "promotion" ? "bg-green-100 text-green-800" :
                            "bg-red-100 text-red-800"
                          }>
                            {msg.message_type}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[var(--river-stone)] py-8">No messages sent yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Retailers Tab */}
          <TabsContent value="retailers">
            <Card className="bg-white card-shadow border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-[var(--deep-ocean)]">Partner Retailers</CardTitle>
                <Dialog open={retailerDialogOpen} onOpenChange={(open) => {
                  setRetailerDialogOpen(open);
                  if (!open) {
                    setRetailerLogo(null);
                    setLogoPreview(null);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary"
                      onClick={() => {
                        setEditingRetailer(null);
                        setRetailerForm({ name: "", description: "", discount_percent: 10, website: "", location: "" });
                        setRetailerLogo(null);
                        setLogoPreview(null);
                      }}
                      data-testid="add-retailer-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Retailer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingRetailer ? "Edit Retailer" : "Add New Retailer"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRetailerSubmit} className="space-y-4">
                      {/* Logo Upload */}
                      <div className="space-y-2">
                        <Label>Logo</Label>
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                            {logoPreview ? (
                              <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                            ) : editingRetailer?.logo_url ? (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${editingRetailer.logo_url}`} 
                                alt="Current logo" 
                                className="w-full h-full object-contain" 
                              />
                            ) : (
                              <Store className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              className="hidden"
                              id="logo-upload"
                              data-testid="retailer-logo-input"
                            />
                            <label htmlFor="logo-upload">
                              <Button type="button" variant="outline" className="cursor-pointer" asChild>
                                <span>
                                  <Upload className="w-4 h-4 mr-2" />
                                  {logoPreview || editingRetailer?.logo_url ? "Change Logo" : "Upload Logo"}
                                </span>
                              </Button>
                            </label>
                            <p className="text-xs text-[var(--river-stone)] mt-1">JPG, PNG, SVG. Max 2MB.</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input
                          value={retailerForm.name}
                          onChange={(e) => setRetailerForm({ ...retailerForm, name: e.target.value })}
                          className="input-field"
                          required
                          data-testid="retailer-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={retailerForm.description}
                          onChange={(e) => setRetailerForm({ ...retailerForm, description: e.target.value })}
                          className="input-field"
                          data-testid="retailer-description-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Discount %</Label>
                          <Input
                            type="number"
                            value={retailerForm.discount_percent}
                            onChange={(e) => setRetailerForm({ ...retailerForm, discount_percent: parseInt(e.target.value) })}
                            className="input-field"
                            data-testid="retailer-discount-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Location</Label>
                          <Input
                            value={retailerForm.location}
                            onChange={(e) => setRetailerForm({ ...retailerForm, location: e.target.value })}
                            className="input-field"
                            data-testid="retailer-location-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={retailerForm.website}
                          onChange={(e) => setRetailerForm({ ...retailerForm, website: e.target.value })}
                          className="input-field"
                          placeholder="https://"
                          data-testid="retailer-website-input"
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary" data-testid="save-retailer-btn">
                        {editingRetailer ? "Update Retailer" : "Add Retailer"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {retailers.map((retailer) => (
                    <div key={retailer.id} className="flex items-center justify-between p-4 bg-[var(--bg-main)] rounded-lg">
                      <div className="flex items-center gap-4">
                        {/* Retailer Logo */}
                        <div className="w-14 h-14 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {retailer.logo_url ? (
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${retailer.logo_url}`} 
                              alt={retailer.name}
                              className="w-full h-full object-contain p-1"
                            />
                          ) : (
                            <Store className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-[var(--deep-ocean)]">{retailer.name}</h4>
                          <p className="text-sm text-[var(--river-stone)]">{retailer.location} | {retailer.discount_percent}% discount</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingRetailer(retailer);
                            setRetailerForm({
                              name: retailer.name,
                              description: retailer.description || "",
                              discount_percent: retailer.discount_percent,
                              website: retailer.website || "",
                              location: retailer.location || ""
                            });
                            setLogoPreview(null);
                            setRetailerLogo(null);
                            setRetailerDialogOpen(true);
                          }}
                          data-testid={`edit-retailer-${retailer.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500"
                          onClick={() => handleDeleteRetailer(retailer.id)}
                          data-testid={`delete-retailer-${retailer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Q&A Tab */}
          <TabsContent value="qa">
            <Card className="bg-white card-shadow border-0">
              <CardHeader>
                <CardTitle className="text-[var(--deep-ocean)]">Q&A Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {questions.length > 0 ? questions.map((q) => (
                    <QAItem key={q.id} question={q} onRespond={handleRespondToQuestion} />
                  )) : (
                    <p className="text-center text-[var(--river-stone)] py-8">No questions yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catches Tab */}
          <TabsContent value="catches">
            <Card className="bg-white card-shadow border-0">
              <CardHeader>
                <CardTitle className="text-[var(--deep-ocean)]">Recent Catches (Sorted by Votes)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {[...catches].sort((a, b) => (b.votes || 0) - (a.votes || 0)).map((catchItem) => (
                    <div key={catchItem.id} className={`p-4 bg-[var(--bg-main)] rounded-lg ${catchItem.is_member_of_month_catch ? "ring-2 ring-yellow-400" : ""}`}>
                      {catchItem.photo_url && (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${catchItem.photo_url}`}
                          alt={catchItem.species}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      )}
                      <h4 className="font-semibold text-[var(--deep-ocean)]">{catchItem.species}</h4>
                      <p className="text-sm text-[var(--river-stone)]">by {catchItem.user_name}</p>
                      <p className="text-xs text-[var(--river-stone)]">
                        {catchItem.location} | {catchItem.likes || 0} likes | <strong>{catchItem.votes || 0} votes</strong>
                      </p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {!catchItem.is_member_of_month_catch && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSetMemberOfMonth(catchItem.user_id, catchItem.id)}
                            data-testid={`set-winning-catch-${catchItem.id}`}
                          >
                            <Award className="w-3 h-3 mr-1" /> Set as Winner
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteCatch(catchItem.id, catchItem.species, catchItem.user_name)}
                          data-testid={`delete-catch-${catchItem.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Q&A Item Component
const QAItem = ({ question, onRespond }) => {
  const [response, setResponse] = useState("");
  const [showInput, setShowInput] = useState(false);

  const handleSubmit = () => {
    if (response.trim()) {
      onRespond(question.id, response);
      setResponse("");
      setShowInput(false);
    }
  };

  return (
    <div className="p-4 bg-[var(--bg-main)] rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-semibold text-[var(--deep-ocean)]">{question.subject}</h4>
          <p className="text-xs text-[var(--river-stone)]">
            From: {question.user_name} ({question.user_email})
          </p>
        </div>
        <Badge className={question.status === "answered" ? "bg-[var(--success)]" : "bg-yellow-500"}>
          {question.status}
        </Badge>
      </div>
      <p className="text-sm text-[var(--river-stone)] mb-3">{question.message}</p>
      
      {question.admin_response ? (
        <div className="p-3 bg-white rounded border-l-4 border-[var(--deep-ocean)]">
          <p className="text-sm text-[var(--river-stone)]">{question.admin_response}</p>
        </div>
      ) : (
        <>
          {showInput ? (
            <div className="space-y-2">
              <Textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Type your response..."
                className="input-field"
                data-testid={`qa-response-input-${question.id}`}
              />
              <div className="flex gap-2">
                <Button size="sm" className="btn-primary" onClick={handleSubmit} data-testid={`send-qa-response-${question.id}`}>
                  <Send className="w-3 h-3 mr-1" /> Send
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowInput(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setShowInput(true)} data-testid={`respond-qa-${question.id}`}>
              Respond
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
